import { useProUser } from "@/contexts/pro-user-context";
import { createCheckoutSession } from "@/lib/payments";
import { track } from "@masterkey/analytics/client";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Clock, Info, X } from "lucide-react";
import { useState } from "react";
import { useToast } from "./ui/use-toast";

import {
  PREMIUM_FEATURES,
  type PlanId,
  getGrowthPlansByInterval,
  getStarterPlansByInterval,
} from "@/lib/constants/pricing";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function SubscriptionPlansModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [isYearly, setIsYearly] = useState(true);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const { toast } = useToast();
  const { tier: subscriptionTier, isTrialActive } = useProUser();
  const starterPlans = getStarterPlansByInterval();
  const growthPlans = getGrowthPlansByInterval();
  const currentStarterPrice = isYearly ? starterPlans[1] : starterPlans[0];
  const currentGrowthPrice = isYearly ? growthPlans[1] : growthPlans[0];

  // Check if user has a paid plan that matches starter or growth
  const isCurrentPlanStarter = String(subscriptionTier) === "starter";
  const isCurrentPlanGrowth = String(subscriptionTier) === "growth";

  // Only consider as having an active subscription if they're on a paid plan (not a trial)
  const hasActiveSubscription =
    (isCurrentPlanStarter || isCurrentPlanGrowth) && !isTrialActive;

  async function handlePlanSelect(priceId: string, planId: PlanId) {
    try {
      setIsLoading(priceId);
      track({ event: "upgrade-intent", source: "subscription-plans-modal" });
      await createCheckoutSession({
        priceId,
        plan: planId,
        isTrial: false,
      });
      return;
    } catch (error) {
      console.error("Error during checkout:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to start checkout process. Please try again.",
        variant: "destructive",
      });
      setIsLoading(null);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:h-screen md:h-auto md:max-w-[800px] lg:max-w-[900px] p-0 overflow-hidden sm:max-h-[100dvh] md:max-h-[90vh] w-full overflow-y-auto md:rounded-3xl rounded-none">
        <div className="px-4 sm:px-6 py-8 relative">
          {/* Early Bird banner */}
          <div className="flex justify-center mb-6">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="group px-6 py-2 bg-brand text-white text-sm font-semibold rounded-full shadow-md flex items-center gap-2 cursor-help transition-shadow hover:shadow-lg">
                    <Clock className="h-4 w-4" />
                    <span>Early Bird: Lock in 50% off forever</span>
                    <Info className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  sideOffset={5}
                  className="max-w-[250px] text-center p-3"
                >
                  <p className="font-semibold mb-1">Limited time offer!</p>
                  <p className="text-primary-foreground/80">
                    Subscribe now to lock in current pricing. Prices will double
                    after beta period ends.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <DialogHeader className="text-center mb-8 mx-auto max-w-xl">
            <DialogTitle className="text-3xl font-gambarino tracking-tight text-noir-900 text-center">
              Choose Your Plan
            </DialogTitle>
            <p className="text-base text-noir-700 mx-auto mt-2">
              Find the perfect tier to automatically uncover high-intent
              customer opportunities from Reddit & Twitter.
            </p>
          </DialogHeader>

          {/* Pricing Toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex p-1 bg-white rounded-full shadow-lg">
              <motion.div
                className="absolute bg-primary h-9 rounded-full"
                initial={false}
                animate={{
                  x: isYearly ? "100%" : "0%",
                  width: "108px",
                }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
              <button
                onClick={() => setIsYearly(false)}
                className={`relative px-8 py-2 rounded-full text-sm font-semibold z-10 transition-colors duration-200 ${
                  !isYearly ? "text-white" : "text-noir-600 hover:text-noir-800"
                }`}
                type="button"
              >
                Monthly
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`relative px-8 py-2 rounded-full text-sm font-semibold z-10 transition-colors duration-200 ${
                  isYearly ? "text-white" : "text-noir-600 hover:text-noir-800"
                }`}
                type="button"
              >
                Yearly
                <span className="absolute -top-3 -right-2 px-2 py-0.5 text-xs bg-brand text-white rounded-full shadow-md">
                  Save 17%
                </span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid sm:grid-cols-2 gap-6 mb-6">
            {/* Starter Plan (Early Bird) */}
            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-3xl p-6 shadow-xl border border-pearl-400/30 relative overflow-hidden"
            >
              {/* Early Bird Badge */}
              <div className="absolute -right-12 top-7 bg-gradient-to-r from-brand to-brand/90 text-white text-xs font-semibold px-12 py-1 rotate-45 shadow-md">
                Early Bird
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-primary">Starter</h3>
                <p className="mt-1 text-primary text-sm">
                  For individuals & small teams
                </p>
                <div className="mt-4">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={isYearly ? "yearly-starter" : "monthly-starter"}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="text-4xl font-bold text-noir-900 relative">
                        <span className="relative">
                          ${currentStarterPrice.monthlyPrice}
                          <span className="text-lg font-normal text-primary ml-1">
                            /mo
                          </span>
                          <span className="absolute text-xs text-noir-500 -right-12 top-1/2 -translate-y-1/2 line-through">
                            ${currentStarterPrice.monthlyPrice * 2}
                          </span>
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-noir-600">
                        {isYearly ? "Billed yearly" : "Billed monthly"}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              <button
                onClick={() =>
                  handlePlanSelect(
                    currentStarterPrice.priceId,
                    isYearly ? "starter_yearly" : "starter_monthly",
                  )
                }
                disabled={isLoading !== null || hasActiveSubscription}
                className="w-full rounded-full px-6 py-3 text-base font-semibold bg-brand text-white transition-all duration-300 hover:bg-brand/90 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                type="button"
              >
                {isLoading === currentStarterPrice.priceId
                  ? "Processing..."
                  : isCurrentPlanStarter && !isTrialActive
                    ? "Current Plan"
                    : isCurrentPlanGrowth && !isTrialActive
                      ? "Already on Growth"
                      : "Upgrade"}
              </button>

              <div className="mt-6 space-y-4">
                {PREMIUM_FEATURES.map((group) => (
                  <div key={group.title}>
                    <div className="text-sm font-semibold text-noir-700 mb-2">
                      {group.title}
                    </div>
                    <ul className="space-y-2">
                      {group.features.map((feature) => (
                        <li
                          key={feature.name}
                          className="flex items-start gap-3"
                        >
                          {feature.starter ? (
                            <Check className="h-5 w-5 text-brand mt-0.5" />
                          ) : (
                            <X className="h-5 w-5 text-noir-300 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-noir-700">
                                {feature.name}
                              </span>
                            </div>
                            {(feature.starter || feature.detail) && (
                              <span className="block text-xs text-noir-500 mt-0.5">
                                {typeof feature.starter === "string"
                                  ? feature.starter
                                  : feature.detail}
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Growth Plan (Early Bird) */}
            <div className="relative">
              {/* Outer motion div for hover effect - NO overflow-hidden */}
              <motion.div whileHover={{ y: -4 }} className="relative h-full">
                {/* Most Popular Badge - positioned relative to the motion div */}
                <div className="absolute -top-3 left-8 z-20">
                  <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-semibold bg-gradient-to-r from-brand to-brand/95 text-white shadow-lg">
                    Most Popular
                  </span>
                </div>

                {/* Inner div for card content, background, and clipping - WITH overflow-hidden */}
                <div
                  className="relative rounded-3xl p-6 shadow-2xl overflow-hidden h-full"
                  style={{
                    background:
                      "linear-gradient(to bottom, hsl(var(--primary)), hsl(var(--primary)/0.9))",
                  }}
                >
                  {/* Early Bird Badge - positioned relative to the inner div, clipped by overflow-hidden */}
                  <div className="absolute -right-11 top-8 bg-gradient-to-r from-brand to-brand/90 text-white text-xs font-semibold px-12 py-1 rotate-45 shadow-md z-10">
                    Early Bird
                  </div>

                  {/* Card Content */}
                  <div className="mb-6 relative z-10">
                    <h3 className="text-xl font-semibold text-white">Growth</h3>
                    <p className="mt-1 text-white/80 text-sm">
                      Scale your outreach
                    </p>

                    <AnimatePresence mode="wait">
                      <motion.div
                        key={isYearly ? "yearly-growth" : "monthly-growth"}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.2 }}
                        className="mt-4"
                      >
                        <div className="text-4xl font-bold text-white relative">
                          <span className="relative">
                            ${currentGrowthPrice.monthlyPrice}
                            <span className="text-lg font-normal text-white/70 ml-1">
                              /mo
                            </span>
                            <span className="absolute text-xs text-white/70 -right-12 top-1/2 -translate-y-1/2 line-through">
                              ${currentGrowthPrice.monthlyPrice * 2}
                            </span>
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-white/70">
                          {isYearly ? "Billed yearly" : "Billed monthly"}
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  <button
                    onClick={() =>
                      handlePlanSelect(
                        currentGrowthPrice.priceId,
                        isYearly ? "growth_yearly" : "growth_monthly",
                      )
                    }
                    disabled={
                      isLoading !== null ||
                      (isCurrentPlanGrowth && !isTrialActive)
                    }
                    className="w-full rounded-full px-6 py-3 text-base font-bold bg-gradient-to-r from-brand to-brand/90 text-white transition-all duration-300 hover:from-brand hover:to-brand/80 hover:shadow-[0_0_20px_rgba(147,51,234,0.3)] transform hover:-translate-y-0.5 flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none relative z-10"
                    type="button"
                  >
                    <span>
                      {isLoading === currentGrowthPrice.priceId
                        ? "Processing..."
                        : isCurrentPlanGrowth && !isTrialActive
                          ? "Current Plan"
                          : "Upgrade"}
                    </span>
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>

                  <div className="mt-6 space-y-4 relative z-10">
                    {PREMIUM_FEATURES.map((group) => (
                      <div key={group.title}>
                        <div className="text-sm font-semibold text-white/90 mb-2">
                          {group.title}
                        </div>
                        <ul className="space-y-2">
                          {group.features.map((feature) => (
                            <li
                              key={feature.name}
                              className="flex items-start gap-3"
                            >
                              <div className="h-5 w-5 rounded-full bg-gradient-to-r from-brand to-brand/90 flex items-center justify-center flex-shrink-0 mt-0.5 transition-transform duration-200 hover:scale-105 shadow-sm">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold text-white">
                                    {feature.name}
                                  </span>
                                  {feature.comingSoon && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white/10 text-white border border-white/20">
                                      Coming Soon
                                    </span>
                                  )}
                                </div>
                                {(typeof feature.growth === "string" ||
                                  feature.detail) && (
                                  <span className="block text-xs text-white/70 mt-0.5">
                                    {typeof feature.growth === "string"
                                      ? feature.growth
                                      : feature.detail}
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
